import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { AlertCircle, FileText, Film, Loader2, Music, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFilePickerStore } from "../store/file-picker-store";
import type { PickerUpload } from "../types/file-picker-types";

const UploadIcon = ({ mimeType }: { mimeType: string }) => {
  if (mimeType.startsWith("video/")) return <Film className="w-6 h-6" />;
  if (mimeType.startsWith("audio/")) return <Music className="w-6 h-6" />;
  return <FileText className="w-6 h-6" />;
};

/** Yüklenmekte olan (ya da yüklenemeyen) dosyanın geçici kartı. */
export const PickerUploadTile = ({ upload }: { upload: PickerUpload }) => {
  const { t } = useTranslation();
  const removeUpload = useFilePickerStore((state) => state.removeUpload);
  const isError = upload.status === "error";

  return (
    <div
      className={cn(
        "relative aspect-square overflow-hidden rounded-2xl bg-bg-secondary dark:bg-gray-800",
        isError ? "ring-2 ring-red-500/70" : "ring-1 ring-black/5 dark:ring-white/5"
      )}
    >
      {upload.previewUrl ? (
        <img src={upload.previewUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-text-tertiary dark:text-gray-400">
          <UploadIcon mimeType={upload.mimeType} />
        </div>
      )}

      <div
        className={cn(
          "absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center backdrop-blur-[2px]",
          isError ? "bg-red-950/60" : "bg-black/45"
        )}
      >
        {isError ? (
          <AlertCircle className="w-6 h-6 text-red-200" />
        ) : (
          <Loader2 className="w-6 h-6 text-white animate-spin" />
        )}
        <span className="text-[11px] font-medium leading-tight text-white line-clamp-2">
          {isError ? upload.error : t("filePicker.uploading")}
        </span>
        <span className="max-w-full truncate text-[10px] text-white/70">{upload.name}</span>
      </div>

      {!isError && (
        <div className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-white/15">
          <motion.div
            className="h-full w-1/3 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
            animate={{ x: ["-100%", "300%"] }}
            transition={{ duration: 1.2, ease: "easeInOut", repeat: Infinity }}
          />
        </div>
      )}

      {isError && (
        <button
          type="button"
          onClick={() => removeUpload(upload.id)}
          aria-label={t("common.close")}
          className="absolute top-2 right-2 w-7 h-7 rounded-lg flex items-center justify-center bg-black/40 text-white hover:bg-black/60"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
